var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { action, observable, observe, extendObservable, computed, ObservableMap } from "mobx";
import Debounce from "debounce-decorator";
import { CardType, EventModes, EventsRequestType, FilterPredicate, SubFilterPredicate, GroupingFilterValues, EventTags, EventTypes } from "../data";
import { DateTimeType } from "../typings";
import { EmptyStatisticsData, StreamingFilterBlockConstant, AllMarketsId } from "../constants";
import * as helpers from "../helpers";
import { mapKickOffTime } from "../composables/mappers";
import { sortEventsByTime, sortLeagues } from "../composables/sorters";
import { buildDateTimeFormatter, buildHeadingsDateFormatter, buildPointsFormatter, calculateGameTime, filterOnlyTopLeagues, updateGameTime } from "../helpers";
const secondsInMinute = 60;
export class Parser {
    constructor(retriever, piper, options) {
        this.millisecondsInMinute = 60000;
        this.getRegions = () => {
            return this.retriever.activeRequests.fixtures
                ? this.retriever.activeRequests.fixtures.request.regionIds
                : (this.retriever.activeRequests.outrights
                    ? this.retriever.activeRequests.outrights.request.regionIds
                    : []);
        };
        this.mapLeagues = (league, regions) => {
            if (this.sortIPTopLeaguesFirst) {
                let isFromRegion = regions.indexOf(league.regionId) >= 0;
                helpers.updateLeagueRankID(league, isFromRegion);
            }
            this.mapLeagueLinkUrl(league);
            return league;
        };
        this.eventHandlers = {
            add: (change) => {
                if (change.type === "add") {
                    const event = this.eventsEnhancements([change.newValue])[0];
                    if (event) {
                        this.addingNewGameTime(event);
                        this.events.push(event);
                        if (this.isPersonalizedHighlights) {
                            this.events = this.sliceForYouEvents(this.events);
                        }
                        else if (this.retriever.dataProvider.isTopLeaguesOnly) {
                            this.events = filterOnlyTopLeagues(this.events, this.getRegions(), this.retriever.dataProvider.isTopLeaguesOnly);
                        }
                    }
                }
            },
            delete: (change) => {
                if (change.type === "delete") {
                    const indexToDelete = this.events.findIndex(e => e.id === change.oldValue.id);
                    this.events.splice(indexToDelete, 1);
                }
            },
            update: (change) => {
                if (change.type === "update") {
                    this.addingNewGameTime(change.newValue);
                    const event = this.eventsEnhancements([change.newValue])[0];
                    if (event) {
                        const indexToUpdate = this.events.findIndex(e => e.id === change.newValue.id);
                        this.events[indexToUpdate] = event;
                    }
                }
            }
        };
        this.piper = piper;
        this.retriever = retriever;
        this.dict = options.dict;
        this.topicPrefix = `${options.parserCmsConfig.topicPrefix}.`;
        this.clientId = options.parserCmsConfig.clientId;
        this.trackingId = options.parserCmsConfig.trackingId;
        this.eventsCountBySport = {};
        this.eventsDisposers = [];
        this.loaderDisposers = [];
        this.liveScoreTimeCollection = [];
        this.markets = observable([]);
        this.events = observable([]);
        this.sports = options.requestParams.sportIds;
        this.dateRange = options.requestParams.dateRange;
        this.allSportIds = options.requestParams.allSportIds;
        this.isFavoritesEnabled = retriever.dataProvider.isFavoritesEnabled;
        this.loader = retriever.loader();
        this.isInFavoritesState = false;
        this.isYourBetModeEnabled = false;
        this.useMonthName = retriever.dataProvider.useMonthName;
        this.localizationLib = options.localizationLib;
        this.formatDate = buildDateTimeFormatter(DateTimeType.date, options.parserCmsConfig.hourFormat, this.useMonthName, false, this.localizationLib.getDateTimeFormatter, true);
        this.formatTime = buildDateTimeFormatter(DateTimeType.time, options.parserCmsConfig.hourFormat, this.useMonthName, false, this.localizationLib.getDateTimeFormatter, true);
        this.formatHeadingDate = buildHeadingsDateFormatter(this.localizationLib.getDateTimeFormatter);
        this.formatPoints = buildPointsFormatter(this.localizationLib.getPointsFormat);
        this.filterManager = retriever.filterManager;
        this.activeEventId = "";
        this.activeMarketIds = [];
        this.betslipItems = observable.map({});
        this.onLogin = this.onLogin.bind(this);
        this.favoritesManager = options.favoritesManager || new helpers.FavoritesManager(`favoriteEvents_${options.parserCmsConfig.topicPrefix}`);
        this.oddStyle = 0;
        this.timeZone = 0;
        this.mapEventLinkUrl = helpers.mapLinkUrl.bind(this, options.navFunctions.adjustLivePath, options.navFunctions.adjustSportsPath);
        this.mapLeagueLinkUrl = helpers.mapLeagueUrl.bind(this, options.navFunctions.adjustSportsPath);
        this.mapStatisticsIcon = helpers.mapStatisticsIcon.bind(this, options.parserCmsConfig.showStatisticsIcon);
        this.mapMBNMarketIcon = helpers.mapMBNMarketIcon.bind(this, options.parserCmsConfig.isMBNEnabled);
        this.specialLeagues = options.specialLeagues;
        this.hasSettlementDate = options.parserCmsConfig.hasSettlementDate;
        this.sortIPTopLeaguesFirst = options.parserCmsConfig.sortIPTopLeaguesFirst;
        this.messageBus = new helpers.MessageBus(this.topicPrefix, this);
        this.shouldSortEventsByTime = options.parserCmsConfig.shouldSortEventsByTime;
        this.isMarketsDropdownFilterEnabled = options.parserCmsConfig.isMarketsDropdownFilterEnabled;
        this.outrightSelectionSorter = new helpers.OutrightSelectionSorter(options.parserCmsConfig.outrightSortOptions);
        this.isLiveSchedule = options.parserCmsConfig.isLiveSchedule;
        this.selectedDay = 0;
        this.piper.appendAll(["mapGameScore", "mapGamePart", "mapEventIcons", "mapParticipants", "mapTeamNames", "mapStatistics", "mapKickOffState", "mapAggregatedEventsCount", "mapGameTime", "filterExpiredOutrights"]);
        this.intervalForUpdateClock = 0;
        this.shouldSortEventsByTime && this.piper.append("sortEventsByTime");
        this.marketTemplateMap = helpers.convertConfig(this.retriever.cardConfig.getConfig(false));
        this.marketTemplateMiniCardMap = this.retriever.cardConfig.getConfig(true) ? helpers.convertConfig(this.retriever.cardConfig.getConfig(true)) : new Map();
        this.currentFillteringForLiveSchedule = this.retriever.cmsConfig.GroupingFilterDefaultValue === GroupingFilterValues.LiveStreaming ? "LiveStreaming" : "All";
    }
    get isMiniEventCard() {
        return this.retriever.isMiniEventCard;
    }
    ;
    get allEventsLoaded() {
        return this.retriever.allEventsLoaded;
    }
    ;
    /**
     * Base functions
     */
    activate() {
        this.intervalForUpdateClock = window.setInterval(this.updateTimeOfLiveEvents, 1000);
        this.messageBus.attachListeners();
        this.attachAllObservables();
        if (this.isLiveSchedule) {
            this.dateRange = this.retriever.initialDateRange;
        }
        this.retriever.activate();
        this.isFavoritesEnabled && this.favoritesManager.loadFavorites();
        this.messageBus.requestGlobalListeners(this.timeZoneChange.bind(this), this.oddStyleChange.bind(this), this.onLogin.bind(this));
        this.messageBus.sendBsUtilStatusMessage();
    }
    deactivate() {
        this.sports = [];
        this.retriever.deactivate();
        this.dettachAllObservables();
        this.messageBus.dettachListeners();
        this.clearData();
        window.clearInterval(this.intervalForUpdateClock);
    }
    setParams(sportIds) {
        this.sports = sportIds;
        this.dateRange = undefined;
        this.retriever.setParams(sportIds);
    }
    get liveScheduleHasLiveStreaming() {
        return this.retriever.eventsGoingLiveWithLiveStreaming;
    }
    /**
     *  Attach/dettach observers to data
     */
    // TODO: observe only what is shown on the screen
    attachEventObservables() {
        this.eventsDisposers = [
            this.retriever.data.events.observe(this.eventsObserver),
            this.retriever.data.markets.observe(this.marketsObserver)
        ];
        //this.eventsDisposers.push.apply([...this.retriever.data.markets.values()].map(x => observe(x, this.marketsObserver)))
    }
    dettachEventObservables() {
        this.eventsDisposers.forEach(x => typeof x === "function" && x());
        this.eventsDisposers = [];
    }
    attachAllObservables() {
        this.attachEventObservables();
        Object.keys(this.retriever.subLoaders).forEach(type => {
            this.loaderDisposers.push(observe(this.retriever.subLoaders[type], this.updateLoader));
        });
        this.loaderDisposers.push(observe(this.retriever, "liveScheduleFilter", (_) => {
            if (this.isLiveSchedule) {
                this.currentFillteringForLiveSchedule = this.retriever.liveScheduleFilter === GroupingFilterValues.LiveStreaming ? "LiveStreaming" : "All";
                this.events = this.eventsEnhancements([...this.retriever.data.events.values()]);
            }
        }));
    }
    dettachAllObservables() {
        this.loaderDisposers.forEach(x => typeof x === "function" && x());
        this.loaderDisposers = [];
        this.dettachEventObservables();
    }
    eventsEnhancements(events, isForYouTab = false) {
        return this.mapEvents(this.piper.executePipeline(this.filterEvents(events, isForYouTab)));
    }
    marketEnhancements(newMarkets) {
        const cardConfig = this.retriever.cardConfig.getConfig(this.isMiniEventCard);
        const marketsToDisplay = helpers.prepareMarkets(this.formatPoints, this.filterMarkets(newMarkets), cardConfig, this.specialLeagues, this.events, this.outrightSelectionSorter, this.isMiniEventCard ? this.marketTemplateMiniCardMap : this.marketTemplateMap);
        return this.mapMarkets(marketsToDisplay);
    }
    addingNewGameTime(event) {
        if (event.liveGameState && event.liveGameState.gameTime) {
            const ev = this.liveScoreTimeCollection.find(x => x.id === event.id);
            if (!ev) {
                this.liveScoreTimeCollection.push({ id: event.id, liveGameState: { gameTime: event.liveGameState.gameTime } });
            }
            else {
                ev.liveGameState.gameTime = event.liveGameState.gameTime;
            }
        }
    }
    updateTimeOfLiveEvents() {
        this.liveScoreTimeCollection.forEach(ev => {
            const event = this.retriever.data.events.get(ev.id);
            if (event && event.liveGameState && event.liveGameState.gameTime && event.liveGameState.clockRunning) {
                ev.liveGameState.gameTime = calculateGameTime(event, ev.liveGameState.gameTime, 1);
                if (ev.liveGameState.gameTime % secondsInMinute === 0) {
                    const event = this.events.find(x => x.id === ev.id);
                    updateGameTime(event, ev.liveGameState.gameTime);
                }
            }
        });
    }
    get isPersonalizedHighlights() {
        return this.retriever.activeRequests.fixtures
            && this.retriever.activeRequests.fixtures.type === EventsRequestType.ByPersonalization
            && this.sports.length > 1;
    }
    get leagues() {
        const regions = this.getRegions() || [];
        const leagues = this.retriever.leagues.map(x => this.mapLeagues(x, regions));
        let topLeagues = [];
        let notTopLeagues = [];
        leagues.forEach(league => this.processTopLeagues(league, regions, topLeagues, notTopLeagues));
        return topLeagues.sort(sortLeagues).concat(notTopLeagues.sort(sortLeagues));
    }
    processTopLeagues(currentLeague, regions, topLeagues, notTopLeagues) {
        if (currentLeague.isTopLeague || regions.indexOf(currentLeague.regionId) >= 0) {
            topLeagues.push(currentLeague);
        }
        else {
            notTopLeagues.push(currentLeague);
        }
    }
    getEventMode(event) {
        let isOutright = event.type === EventTypes.Outright;
        if (event.isLive) {
            return isOutright ? EventModes.LiveOutright : EventModes.LiveFixture;
        }
        else {
            return isOutright ? EventModes.Outright : EventModes.Fixture;
        }
    }
    /**
     * Internal msg bus handlers
     */
    sportsChange(x) {
        if (!this.isInFavoritesState && helpers.compareArrays(x.sportIds, this.sports)) {
            return;
        }
        const querySportUpdate = (shouldChangeMarketConfig) => {
            let tags = [];
            if (this.isLiveSchedule) {
                this.currentFillteringForLiveSchedule = this.retriever.cmsConfig.GroupingFilterDefaultValue === GroupingFilterValues.LiveStreaming ? "LiveStreaming" : "All";
                tags = this.retriever.cmsConfig.GroupingFilterDefaultValue === GroupingFilterValues.LiveStreaming ? [EventTags.LiveStreaming] : [];
            }
            this.retriever.querySportUpdate(x.sportIds, shouldChangeMarketConfig, tags);
        };
        this.isInFavoritesState = false;
        this.sports = x.sportIds;
        this.setSubFilterPredicate(SubFilterPredicate.None);
        this.executeQuery(querySportUpdate);
        //personalized highlight (for you tab) should reset data
        const isForYouTab = x.sportIds.length > 1;
        if (isForYouTab) {
            this.retriever.clearData();
            this.clearData();
            this.executeQuery(querySportUpdate);
        }
        this.events = this.eventsEnhancements([...this.retriever.data.events.values()], isForYouTab);
    }
    leaguesChange(x) {
        const queryLeaguesUpdate = (shouldChangeMarketConfig) => this.retriever.queryLeaguesUpdate(x.leagueIds, this.sports, shouldChangeMarketConfig);
        this.isInFavoritesState = false;
        this.sports = [x.sportId];
        this.retriever.clearData();
        this.clearData();
        this.executeQuery(queryLeaguesUpdate);
    }
    regionsChange(x) {
        const queryRegionIdUpdate = (shouldChangeMarketConfig) => this.retriever.queryRegionIdUpdate(x.regionId, x.sportId, shouldChangeMarketConfig);
        this.isInFavoritesState = false;
        this.sports = [x.sportId];
        this.retriever.clearData();
        this.clearData();
        this.executeQuery(queryRegionIdUpdate);
    }
    datesChange(x) {
        var _a, _b, _c, _d;
        if (((_a = x.dateRange.from) === null || _a === void 0 ? void 0 : _a.getTime()) !== ((_c = (_b = this.dateRange) === null || _b === void 0 ? void 0 : _b.from) === null || _c === void 0 ? void 0 : _c.getTime())
            || x.dateRange.timeRange !== ((_d = this.dateRange) === null || _d === void 0 ? void 0 : _d.timeRange)) {
            const queryDatesUpdate = (shouldChangeMarketConfig) => {
                let tags = [];
                if (this.isLiveSchedule) {
                    this.currentFillteringForLiveSchedule = this.retriever.cmsConfig.GroupingFilterDefaultValue === GroupingFilterValues.LiveStreaming ? "LiveStreaming" : "All";
                    tags = this.retriever.cmsConfig.GroupingFilterDefaultValue === GroupingFilterValues.LiveStreaming ? [EventTags.LiveStreaming] : [];
                }
                this.retriever.queryDatesUpdate(x.dateRange, this.sports, shouldChangeMarketConfig, tags);
            };
            this.isInFavoritesState = false;
            this.dateRange = x.dateRange;
            this.events = this.eventsEnhancements([...this.retriever.data.events.values()]);
            this.executeQuery(queryDatesUpdate);
        }
        if (x.fixturesCount) {
            this.eventsCountBySport[this.sports[0]] = x.fixturesCount;
        }
    }
    liveStreamChange() {
        if (this.filterManager.isCurrentMainFilter(FilterPredicate.LiveStreaming)) {
            return;
        }
        this.activeMarketIds.splice(0, this.activeMarketIds.length);
        this.isInFavoritesState = false;
        if (!this.isLiveSchedule) {
            this.sports = this.allSportIds;
            this.dateRange = undefined;
        }
        this.currentFillteringForLiveSchedule = "LiveStreaming";
        this.retriever.queryTagsUpdate(EventTags.LiveStreaming, !this.isLiveSchedule);
        this.events = this.eventsEnhancements([...this.retriever.data.events.values()]);
        this.markets = this.marketEnhancements([...this.retriever.data.markets.values()]);
    }
    favoritesChange() {
        if (this.filterManager.isCurrentMainFilter(FilterPredicate.Favorites)) {
            return;
        }
        this.isInFavoritesState = true;
        this.sports = this.favoritesManager.favoriteSportIds;
        this.dateRange = undefined;
        this.retriever.queryFavoritesUpdate(this.favoritesManager.getFavorites);
        this.events = this.eventsEnhancements([...this.retriever.data.events.values()]);
    }
    liveStreamingChange() {
        this.setSubFilterPredicate(SubFilterPredicate.LiveStreaming);
        this.events = this.eventsEnhancements([...this.retriever.data.events.values()]);
    }
    fastMarketsChange() {
        this.setSubFilterPredicate(SubFilterPredicate.FastMarket);
        this.events = this.eventsEnhancements([...this.retriever.data.events.values()]);
    }
    allEventsChange() {
        this.setSubFilterPredicate(SubFilterPredicate.None);
        this.events = this.eventsEnhancements([...this.retriever.data.events.values()]);
    }
    yourBetSwitchChange(msg) {
        this.isYourBetModeEnabled = msg.isYourBetModeEnabled;
    }
    marketGroupsChange(msg) {
        if (this.activeMarketIds.includes(msg.marketId)) {
            return; // TODO: add test
        }
        this.dettachEventObservables();
        this.activeMarketIds = msg.marketId === AllMarketsId
            ? []
            : [msg.marketId];
        this.markets = this.marketEnhancements([...this.retriever.data.markets.values()]);
        this.attachEventObservables();
    }
    activeEventIdChange(eventId) {
        if (!eventId) {
            return;
        }
        this.activeEventId = eventId;
    }
    //TODO: Check if we can do it with less complexity
    selectedMarketIdChange(sportIds, selectedMarketTypeIds) {
        if (selectedMarketTypeIds.every(marketId => this.activeMarketIds.includes(marketId))) {
            return;
        }
        this.activeMarketIds = selectedMarketTypeIds;
        //TODO: Parser shouldn't change state of retriever - pass ids to loadData
        this.retriever.buildMarketIdByType(selectedMarketTypeIds, sportIds);
        this.retriever.loadData();
        this.markets = this.marketEnhancements(this.filterMarkets([...this.retriever.data.markets.values()]));
    }
    selectedDateChange(date, index) {
        this.selectedDay = index;
        this.messageBus.sendDateMessage({
            dateRange: {
                from: new Date(date),
                timeRange: "OneDay"
            }
        });
    }
    onLogin(user) {
        user && this.retriever.setRegionId(user.CountryOfRegistration);
    }
    onLogout() {
        this.retriever.removeRegionId();
    }
    renewJWTToken() {
        this.retriever.onJWTRenew();
    }
    updateMiniEventCard(isMiniEventCard) {
        this.retriever.isMiniEventCard = isMiniEventCard;
    }
    changedToSide(eventCardMessage) {
        this.updateMiniEventCard(eventCardMessage.changedToSide);
        if (!eventCardMessage.changedToSide) {
            this.filterManager.setSubPredicate(SubFilterPredicate.None);
            this.events = this.eventsEnhancements([...this.retriever.data.events.values()]);
        }
        this.events = this.events.map(event => {
            helpers.mapEventComponent(event, this.retriever.cardConfig, this.isMiniEventCard); // TODO: add test
            helpers.mapIsSelected(event, this.activeEventId); // TODO: add test
            return event; // TODO: add test
        });
        if (eventCardMessage.initActiveEvent && !this.activeEventId && this.events.length) {
            const firstEvent = this.events[0]; // TODO: add test
            const isOutright = firstEvent.type === EventTypes.Outright; // TODO: add test
            this.activeEventId = firstEvent.id; // TODO: add test
            this.messageBus.sendActiveEventMessage(this.activeEventId, isOutright); // TODO: add test
        }
        let activeEvent = this.events.find(e => e.id === this.activeEventId);
        if (activeEvent) {
            this.messageBus.sendLastActiveEventMessage(activeEvent.id, this.getEventMode(activeEvent)); // TODO: add test
        }
        this.sendTagsMessage(this.events);
    }
    betslipAddedItemsReceived(message) {
        this.dettachEventObservables();
        helpers.excludeYourBetItems(message.items).forEach((item) => {
            const market = this.markets.find(m => m.id === item.marketId);
            const selection = market && market.selections.find(s => s.id === item.selectionId);
            this.betslipItems.set(`${item.marketId}_${item.selectionId}`, true);
            if (selection) {
                selection.isSelectionInBetslip = true; // TODO: add test
            }
        });
        this.attachEventObservables();
    }
    betslipRemovedItemsReceived(message) {
        this.dettachEventObservables();
        helpers.excludeYourBetItems(message.items).forEach((item) => {
            let market = this.markets.find(m => m.id === item.marketId); // TODO: add test
            let selection = market && market.selections.find(s => s.id === item.selectionId); // TODO: add test
            this.betslipItems.set(`${item.marketId}_${item.selectionId}`, false); // TODO: add test
            if (selection) { // TODO: add test
                selection.isSelectionInBetslip = false; // TODO: add test
            }
        });
        this.attachEventObservables();
    }
    betslipClearedItemsReceived() {
        this.dettachEventObservables();
        this.betslipItems.clear();
        this.attachEventObservables();
    }
    betSlipStatusReceived(message) {
        this.dettachEventObservables();
        this.betslipItems.clear();
        if (message) {
            helpers.excludeYourBetItems(message.items).forEach((item) => {
                let market = this.markets.find(m => m.id === item.marketId); // TODO: add test
                let selection = market && market.selections.find(s => s.id === item.selectionId); // TODO: add test
                this.betslipItems.set(`${item.marketId}_${item.selectionId}`, true); // TODO: add test
                if (selection) { // TODO: add test
                    selection.isSelectionInBetslip = true; // TODO: add test
                }
            });
        }
        this.attachEventObservables();
    }
    oddStyleChange(oddStyleMsg) {
        this.oddStyle = oddStyleMsg.currentSelectedOddStyle;
        this.markets.forEach(market => {
            market.selections = helpers.mapSelectionsOdds(market.selections, this.oddStyle, this.dict);
        });
    }
    timeZoneChange(timeZoneMsg) {
        this.timeZone = timeZoneMsg.currentSelectedTimeZone;
        if (this.isLiveSchedule && this.dateRange && this.dateRange.from) {
            const days = this.next7Days(this.getBeggingOfDay(this.timeZone, new Date()));
            this.datesChange({
                dateRange: {
                    from: days[this.selectedDay],
                    timeRange: "OneDay"
                }
            });
            this.events = this.events = this.eventsEnhancements([...this.retriever.data.events.values()]);
        }
        if (!this.isLiveSchedule) {
            this.events = this.events.map(ev => this.updateEventTime(ev));
        }
    }
    mapEvents(events) {
        return events.map((ev) => {
            // add events missing properties
            this.updateEventTime(ev);
            this.mapIsSelected(ev);
            this.mapEventLinkUrl(ev);
            this.mapFavorites(ev);
            this.mapEventComponent(ev);
            this.mapStatisticsIcon(ev);
            return ev;
        });
    }
    mapIsSelected(ev) {
        helpers.mapIsSelected(ev, this.activeEventId);
    }
    mapFavorites(ev) {
        if (this.isFavoritesEnabled) {
            ev.isInFavorites = this.favoritesManager.isInFavorites(ev.id);
        }
        return ev;
    }
    mapEventComponent(ev) {
        helpers.mapEventComponent(ev, this.retriever.cardConfig, this.isMiniEventCard);
    }
    updateEventTime(ev) {
        mapKickOffTime(ev, this.timeZone);
        helpers.mapStartDateTime(ev, {
            currentTimeZone: this.timeZone,
            formatDate: this.formatDate,
            formatTime: this.formatTime,
            dict: this.dict,
            hasSettlementDate: this.hasSettlementDate
        });
        return ev;
    }
    mapMarkets(updatedMarkets) {
        return updatedMarkets.map(m => {
            const sel = helpers.mapSelectionsOdds(m.selections, this.oddStyle, this.dict);
            m.selections = sel;
            this.addSelectionBetslipStatusProperty(m);
            this.mapMBNMarketIcon(m);
            return m;
        });
    }
    updateLoader() {
        this.loader = this.retriever.loader();
        if (!this.loader.isLoading && this.loader.isFullLoadFinished) {
            const filteredEvents = this.filterEvents([...this.retriever.data.events.values()]);
            const eventsCount = this.sports.length
                ? this.eventsCountBySport[this.sports[0]] || filteredEvents.length
                : filteredEvents.length;
            this.sendEventsCountMessage(eventsCount);
            this.sendTagsMessage(filteredEvents);
            this.messageBus.sendBsUtilStatusMessage();
            this.isLiveSchedule && (this.events = this.events.sort(sortEventsByTime));
        }
    }
    eventsListCountChange(msg) {
        this.eventsCountBySport[msg.sportId] = msg.eventsCount;
    }
    managedEvents(events) {
        let filteredEvents = this.filterEvents(events);
        let eventsToUpdate = this.mapEvents(this.piper.executePipeline(filteredEvents));
        return eventsToUpdate;
    }
    addSelectionBetslipStatusProperty(market) {
        market.selections = market.selections.map(s => {
            const selectionId = `${s.marketId}_${s.id}`;
            extendObservable(s, {
                isSelectionInBetslip: this.betslipItems.get(selectionId) || false
            });
            return s;
        });
    }
    clearEventTags() {
        this.retriever.clearEventTags();
        this.currentFillteringForLiveSchedule = "All";
        this.filterManager.setMainPredicate(FilterPredicate.Date);
        this.events = this.eventsEnhancements([...this.retriever.data.events.values()]);
    }
    /**
     * Filter data collections
     */
    filterEvents(events, isForYouTab = false) {
        let to = this.getDateRangeTo();
        let ids = this.getIds();
        let filterFn = this.getFilterFn(ids, to);
        let filteredEvents = events.filter(filterFn);
        filteredEvents = filterOnlyTopLeagues(filteredEvents, this.getRegions(), this.retriever.dataProvider.isTopLeaguesOnly);
        if (this.isLiveSchedule) {
            filteredEvents = this.liveScheduleFilter(filteredEvents);
        }
        if (isForYouTab) {
            filteredEvents = this.sliceForYouEvents(filteredEvents);
        }
        return filteredEvents.map(x => (Object.assign({}, x)));
    }
    getDateRangeTo() {
        return this.dateRange && this.dateRange.from
            ? helpers.DateRangeMapper[this.dateRange.timeRange](this.dateRange.from)
            : "";
    }
    getIds() {
        return this.filterManager.getMainPredicate() === FilterPredicate.Favorites
            ? this.favoritesManager.favoriteEventIds
            : this.sports;
    }
    getFilterFn(ids, to) {
        return this.filterManager.filterEntity.bind(this.filterManager, ids, this.dateRange && this.dateRange.from, to);
    }
    liveScheduleFilter(filteredEvents) {
        filteredEvents = filteredEvents.filter(x => x.isGoingLive);
        if (this.currentFillteringForLiveSchedule === "LiveStreaming") {
            filteredEvents = filteredEvents.filter(x => x.tags.indexOf(EventTags.LiveStreaming.toString()) > -1
                || !this.retriever.eventsGoingLiveWithLiveStreaming);
        }
        return filteredEvents;
    }
    filterMarkets(markets) {
        // activeMarketId is being set to -1, when no filtration should be applied
        if (this.activeMarketIds.length < 1 || this.activeMarketIds.includes("-1")) {
            return markets;
        }
        return markets.filter(this.retriever.filterByMarketId.bind(this.retriever, this.activeMarketIds));
    }
    executeQuery(queryCallback) {
        const cardConfig = this.getCardConfigBySportId(this.sports[0], this.isMiniEventCard);
        const shouldChangeMarketRequest = this.shouldChangeMarketRequest(cardConfig);
        if (shouldChangeMarketRequest) {
            this.activeMarketIds = [];
        }
        else {
            const marketConfig = cardConfig.markets[0];
            this.activeMarketIds = [marketConfig.idLive, marketConfig.idPreLive];
            this.retriever.buildMarketIdByType(this.activeMarketIds, this.sports);
        }
        queryCallback(shouldChangeMarketRequest);
        if (this.isMarketsDropdownFilterEnabled) {
            this.markets = this.marketEnhancements(this.filterMarkets([...this.retriever.data.markets.values()]));
        }
    }
    shouldChangeMarketRequest(cardConfig) {
        return !(this.isMarketsDropdownFilterEnabled && !this.isMiniEventCard) || cardConfig.cardType !== CardType.Regular || this.isPersonalizedHighlights;
    }
    setSubFilterPredicate(newFilter) {
        if (this.filterManager.isCurrentSubFilter(newFilter)) {
            return;
        }
        this.isInFavoritesState = false;
        this.dateRange = undefined;
        this.filterManager.setSubPredicate(newFilter);
    }
    /**
     * Send messages via msg bus
     */
    sendEventsCountMessage(count) {
        let sport = this.filterManager.getMainPredicate() === FilterPredicate.LiveStreaming
            || this.sports.length === 0
            ? StreamingFilterBlockConstant
            : this.sports[0];
        this.messageBus.sendEventCountMessage(count, sport);
    }
    sendTagsMessage(events) {
        let hasLiveStreamEvents = false;
        let hasFastMarketsEvents = false;
        for (let i = 0; i < events.length; i += 1) {
            const ev = events[i];
            ev.tags.indexOf(EventTags.LiveStreaming) > -1 && (hasLiveStreamEvents = true);
            ev.tags.indexOf(EventTags.FastMarket) > -1 && (hasFastMarketsEvents = true);
            if (hasLiveStreamEvents && hasFastMarketsEvents) {
                break;
            }
        }
        const result = { hasLiveStreamEvents, hasFastMarketsEvents };
        this.messageBus.sendTagsMessage(result);
        return result;
    }
    sendBsUtilItemsAddMessage(items) {
        this.messageBus.sendBsUtilItemsAddMessage({ items, receiverIds: [] }, this.trackingId);
    }
    sendBsUtilItemsRemoveMessage(items) {
        this.messageBus.sendBsUtilItemsRemoveMessage({ items, receiverIds: [] });
    }
    /**
     * Handlers triggered from State
     */
    manageFavorites(event) {
        event.isInFavorites = !event.isInFavorites;
        this.favoritesManager.manageFavorites(event);
    }
    getOpenGameData(event, ev) {
        if (!event) {
            return;
        }
        return {
            event: ev,
            id: parseInt(event.id),
            isLive: event.isLive,
            evTypeForGameBlock: this.getEventMode(event),
            shouldHideSidePanels: false,
            branchID: parseInt(event.sportId)
        };
    }
    openStats(statisticsEventId, e) {
        let statData = this.getStatisticsData(statisticsEventId);
        this.retriever.dataProvider.statisticsProvider && this.retriever.dataProvider.statisticsProvider.openStats(statData, e);
    }
    openLiveStream(eventId) {
        this.messageBus.sendLiveStreamMessage(eventId);
    }
    getStatisticsData(statisticsEventId) {
        return Object.assign(Object.assign({}, EmptyStatisticsData), { betRadarData: {
                clientId: this.clientId,
                eventId: statisticsEventId
            } });
    }
    eventsObserver(change) {
        this.eventHandlers.add(change);
        this.eventHandlers.update(change);
        this.eventHandlers.delete(change);
    }
    marketsObserver(change) {
        this.dettachAllObservables();
        if (change.type === "add") {
            const marketToAdd = this.marketEnhancements([change.newValue])[0];
            if (marketToAdd) {
                this.markets.push(marketToAdd);
            }
        }
        if (change.type === "delete") {
            const indexToDelete = this.markets.findIndex(e => e.id === change.oldValue.id);
            this.markets.splice(indexToDelete, 1);
        }
        if (change.type === "update") {
            const indexToUpdate = this.markets.findIndex(e => e.id === change.newValue.id);
            this.markets[indexToUpdate] = this.marketEnhancements([change.newValue])[0];
        }
        this.attachAllObservables();
    }
    /**
     * Store retrieved data helpers
     */
    getCardConfigBySportId(id, isMiniEventCard) {
        return this.retriever.cardConfig.getCardConfigBySportId(id, isMiniEventCard);
    }
    viewMore() {
        this.retriever.loadMoreData();
    }
    clearData() {
        this.dateRange = undefined;
        this.events = observable([]);
        this.markets = observable([]);
        this.eventsCountBySport = {};
    }
    getBeggingOfDay(timezone, date) {
        const delta = this.getDifferenceBetweenCurrentTimezoneAndSelected(date, timezone);
        const currentInTimezone = new Date(date.getTime() + delta);
        currentInTimezone.setHours(0, 0, 0, 0);
        const target = new Date(currentInTimezone.getTime() - delta);
        return target;
    }
    getDifferenceBetweenCurrentTimezoneAndSelected(currentDate, selectedTimezone) {
        const browserTimezoneOffset = -currentDate.getTimezoneOffset() * this.millisecondsInMinute;
        const targetTimezoneOffset = selectedTimezone * 60 * this.millisecondsInMinute;
        return targetTimezoneOffset - browserTimezoneOffset;
    }
    next7Days(today) {
        let days = [];
        for (let dayCount = 0; dayCount < 7; dayCount++) {
            days.push(helpers.addDays(today, dayCount));
        }
        return days;
    }
    sliceForYouEvents(events) {
        return events.slice(0, this.getForYouMaxIndex(events));
        ;
    }
    getForYouMaxIndex(events) {
        const configuredMaxEvents = this.getEventsRequestMaxEvents();
        if (typeof configuredMaxEvents === "number") {
            return configuredMaxEvents;
        }
        return events.length;
    }
    getEventsRequestMaxEvents() {
        return this.retriever.dataProvider.eventsRequest && this.retriever.dataProvider.eventsRequest.request && this.retriever.dataProvider.eventsRequest.request.numberOfEvents;
    }
}
__decorate([
    observable,
    __metadata("design:type", Array)
], Parser.prototype, "events", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], Parser.prototype, "markets", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], Parser.prototype, "eventsCountBySport", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], Parser.prototype, "sports", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], Parser.prototype, "dateRange", void 0);
__decorate([
    observable,
    __metadata("design:type", Number)
], Parser.prototype, "oddStyle", void 0);
__decorate([
    observable,
    __metadata("design:type", Number)
], Parser.prototype, "timeZone", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], Parser.prototype, "isInFavoritesState", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], Parser.prototype, "loader", void 0);
__decorate([
    observable,
    __metadata("design:type", Boolean)
], Parser.prototype, "isYourBetModeEnabled", void 0);
__decorate([
    observable,
    __metadata("design:type", String)
], Parser.prototype, "activeEventId", void 0);
__decorate([
    observable,
    __metadata("design:type", ObservableMap)
], Parser.prototype, "betslipItems", void 0);
__decorate([
    observable,
    __metadata("design:type", Array)
], Parser.prototype, "activeMarketIds", void 0);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], Parser.prototype, "isMiniEventCard", null);
__decorate([
    computed,
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [])
], Parser.prototype, "allEventsLoaded", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Parser.prototype, "activate", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Parser.prototype, "deactivate", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Parser.prototype, "liveScheduleHasLiveStreaming", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Parser.prototype, "updateTimeOfLiveEvents", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Parser.prototype, "isPersonalizedHighlights", null);
__decorate([
    computed,
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Parser.prototype, "leagues", null);
__decorate([
    action,
    __metadata("design:type", Object)
], Parser.prototype, "mapLeagues", void 0);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Parser.prototype, "sportsChange", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Parser.prototype, "leaguesChange", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Parser.prototype, "regionsChange", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Parser.prototype, "datesChange", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Parser.prototype, "liveStreamChange", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Parser.prototype, "favoritesChange", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Parser.prototype, "liveStreamingChange", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Parser.prototype, "fastMarketsChange", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Parser.prototype, "allEventsChange", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Parser.prototype, "yourBetSwitchChange", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Parser.prototype, "marketGroupsChange", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [String]),
    __metadata("design:returntype", void 0)
], Parser.prototype, "activeEventIdChange", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Array, Array]),
    __metadata("design:returntype", void 0)
], Parser.prototype, "selectedMarketIdChange", null);
__decorate([
    Debounce,
    __metadata("design:type", Object)
], Parser.prototype, 249, void 0);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Parser.prototype, "changedToSide", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Parser.prototype, "betslipAddedItemsReceived", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Parser.prototype, "betslipRemovedItemsReceived", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Parser.prototype, "betslipClearedItemsReceived", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Parser.prototype, "betSlipStatusReceived", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Parser.prototype, "oddStyleChange", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Parser.prototype, "timeZoneChange", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Parser.prototype, "mapFavorites", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Array]),
    __metadata("design:returntype", void 0)
], Parser.prototype, "mapMarkets", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], Parser.prototype, "updateLoader", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Parser.prototype, "eventsListCountChange", null);
__decorate([
    action,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Function]),
    __metadata("design:returntype", void 0)
], Parser.prototype, "executeQuery", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Parser.prototype, "manageFavorites", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Parser.prototype, "eventsObserver", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", void 0)
], Parser.prototype, "marketsObserver", null);
//# sourceMappingURL=parser.js.map